import { useQuery } from '@apollo/client';
import { Redirect, RouteComponentProps, WindowLocation } from '@reach/router';
import * as Sentry from '@sentry/react';
import i18next from 'i18next';
import { useChangeUserLanguage } from 'lib/hooks';
import { useEffect, useMemo } from 'react';
import {
  LOADING_IMG_2,
  useLoader,
} from '../components/LoadingProvider/LoadingProvider';
import { meWithTutorialQuery } from '../graphql/queries';
import { MeWithTutorialQuery, MeWithTutorialQuery_me } from '../graphql/types';
import { AccessDenied } from './AccessDenied';
import Compatibility from './Compatibility';
import ErrorPage from './ErrorPage';

export interface ProtectedRouteComponentProps extends RouteComponentProps {
  user: MeWithTutorialQuery_me;
  isMediumGameActive?: boolean;
  location?: WindowLocation<{ pathname?: string }>;
}

interface Props extends RouteComponentProps {
  component: React.ComponentType<ProtectedRouteComponentProps>;
  location?: WindowLocation<{ pathname?: string }>;
}

const ProtectedRoute = ({ component: Component, ...props }: Props) => {
  const { data, loading, error, refetch } =
    useQuery<MeWithTutorialQuery>(meWithTutorialQuery);

  useLoader(loading, { withDelay: 500, image: LOADING_IMG_2 });

  const [changeLanguage] = useChangeUserLanguage();
  const userId = data?.me?.id;
  const language = data?.me?.language;

  const gamesConfig = data?.me?.company?.gamesConfig;
  const isMediumGameActive = useMemo(() => {
    return (
      gamesConfig?.find((config) => config.code === 'MEDIUM_GAME')?.active ??
      true
    );
  }, [gamesConfig]);

  // Set sentry scope
  useEffect(() => {
    Sentry.configureScope((scope) => {
      scope.setUser({ 'id:': userId });
    });
  }, [userId]);

  useEffect(() => {
    if (language && language !== i18next.languages[0]) {
      i18next.changeLanguage(language);
    } else if (language === null) {
      changeLanguage({
        variables: {
          input:
            data?.me?.company?.defaultLanguage?.code ?? i18next.languages[0],
        },
      });
    }
  }, [language, changeLanguage, data]);

  if (error) {
    // Return some error page. With home and logout
    return (
      <ErrorPage
        error={error}
        onRetry={() => {
          refetch();
        }}
      />
    );
  }
  if (loading) {
    // This should return same loading screen like Suspense in App.tsx, ideally this useQuery should use suspense too
    return <div />;
  }
  if (!data || !data.me) {
    return <Redirect to="/login" noThrow state={{ from: props.location }} />;
  }
  if (!data.me.canAccessGame) {
    return <AccessDenied />;
  }

  return (
    <Compatibility>
      <Component {...props} user={data.me} isMediumGameActive={isMediumGameActive} />
    </Compatibility>
  );
};

export default ProtectedRoute;
